import React, { useState, useEffect, useMemo, useCallback } from "react";
import Link from "next/link";
import "react-tabs/style/react-tabs.css";
import Meta from "../../components/Meta";
import Image from "next/image";
import { errorToast, successToast } from "../../utils/toast";
import { validateEmail } from "../../utils/validation";
import { useRouter } from "next/router";
import { useAuth } from "../../contexts/AuthContext";
import ResendVerificationNotice from "../../components/auth/ResendVerificationNotice";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const router = useRouter();
  const {
    login,
    isAuthenticated,
    processOAuthTokens,
    loading: authLoading,
  } = useAuth();
  const { returnUrl, redirect } = router.query;

  // Memoize redirect path to prevent unnecessary re-renders
  const redirectPath = useMemo(() => {
    return redirect || returnUrl || "/dashboard";
  }, [redirect, returnUrl]);

  // Track if component is mounted
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle authentication redirect - only check if already authenticated on initial mount
  useEffect(() => {
    if (mounted && !authLoading) {
      // Only redirect if user was already authenticated before this page load
      const token = localStorage.getItem("access_token");
      if (token && isAuthenticated()) {
        console.log("User is already authenticated on mount, redirecting...");
        router.replace(redirectPath);
      }
    }
  }, [mounted]); // Only run once on mount

  // Handle OAuth redirect processing
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const handleOAuthRedirect = async () => {
        try {
          const success = await processOAuthTokens(hash);
          window.history.replaceState(null, null, window.location.pathname);

          if (success) {
            successToast("Login successful!");
            // Force immediate redirect for OAuth with window.location backup
            const redirectPath = redirect || returnUrl || "/dashboard";
            router.replace(redirectPath).catch(() => {
              console.log(
                "OAuth router redirect failed, using window.location"
              );
              window.location.href = redirectPath;
            });
          } else {
            errorToast("Failed to process login information");
          }
        } catch (error) {
          console.error("OAuth processing error:", error);
          errorToast("Failed to process login information");
          window.history.replaceState(null, null, window.location.pathname);
        }
      };

      handleOAuthRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirect, returnUrl]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateEmail(email)) {
        errorToast("Please enter a valid email");
        return;
      }

      setIsLoading(true);
      setEmailUnverified(false);
      try {
        const result = await login(email, password);

        if (result.success) {
          successToast("Login successful!");
          // Force immediate redirect with window.location as backup
          const redirectUrl = redirect || returnUrl || "/dashboard";
          router.replace(redirectUrl).catch(() => {
            // If router fails, use window.location
            console.log("Router redirect failed, using window.location");
            window.location.href = redirectUrl;
          });
        } else if (result.errorCode === "EMAIL_NOT_CONFIRMED") {
          setEmailUnverified(true);
          errorToast(
            "Your email address hasn't been verified yet. Please use the link we emailed you."
          );
        } else {
          errorToast(result.error || "Login failed. Please try again.");
        }
      } catch (error) {
        errorToast("Login failed. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, login, router, redirect, returnUrl]
  );

  const handleGoogleSignIn = useCallback(() => {
    const googleCallbackUrl = `${window.location.origin}${router.pathname}`;
    window.location.href = `${process.env.NEXT_PUBLIC_BASE_URL}/api/auth/google?redirectTo=${encodeURIComponent(googleCallbackUrl)}`;
  }, [router.pathname]);

  // Remove duplicate redirect effect - this creates race conditions

  // Don't render if not mounted yet
  if (!mounted) {
    return null;
  }

  return (
    <div>
      <Meta title="SignIn || Impactoverse " />
      <section className="relative min-h-screen">
        <div className="lg:flex min-h-screen">
          <div className="hidden lg:block lg:w-1/2 relative text-center min-h-screen">
            <Image
              width={768}
              height={722}
              src="/images/login.jpg"
              alt="login"
              className="absolute h-full w-full object-cover"
              priority={true}
              placeholder="blur"
              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
            />
            <div className="relative py-16 sm:py-24 lg:py-44 px-5">
              <Link href="/" className="relative">
                <Image
                  src="/images/ico-logo/white.png"
                  priority={true}
                  height={100}
                  width={160}
                  className="mx-auto mb-4 rounded-md animate-spin-faux3d"
                  alt="Impactoverse | Art For Impact"
                />
              </Link>
              <Link href="/" className="relative">
                <Image
                  src="/logo/scale.png"
                  height={80}
                  width={320}
                  className="mx-auto rounded-md animate-spin-faux3d"
                  alt="Impactoverse | Art For Impact"
                />
              </Link>
            </div>
          </div>

          <div className="flex items-center p-4 sm:p-6 lg:p-[5%] lg:w-1/2 min-h-screen lg:min-h-auto">
            <picture className="pointer-events-none absolute inset-0 -z-10 dark:hidden">
              <Image
                width={1519}
                height={773}
                src="/images/gradient_light.jpg"
                alt="gradient"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </picture>

            <div className="w-full max-w-md mx-auto">
              {/* Mobile Logo */}
              <div className="block lg:hidden mb-8 text-center">
                <Link href="/" className="inline-block">
                  <Image
                    src="/logo/black.png"
                    height={60}
                    width={240}
                    className="mx-auto rounded-md dark:hidden"
                    alt="Impactoverse | Art For Impact"
                  />
                  <Image
                    src="/logo/scale.png"
                    height={60}
                    width={240}
                    className="mx-auto rounded-md hidden dark:block"
                    alt="Impactoverse | Art For Impact"
                  />
                </Link>
              </div>

              <div>
                <h2 className="font-display text-grayish text-center text-xl sm:text-2xl lg:text-3xl dark:text-white">
                  Welcome back
                </h2>
                <p className="dark:text-white text-grayish mb-6 sm:mb-10 lg:mb-14 text-center text-sm sm:text-base">
                  Sign in to access your Impact Art
                </p>
              </div>

              <form className="mt-8" onSubmit={handleSubmit}>
                <div className="relative mb-3 sm:mb-4 lg:mb-7">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grayish dark:text-white"
                    width="20"
                    height="17"
                    viewBox="0 0 20 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 16.5C1.45 16.5 0.979167 16.3042 0.5875 15.9125C0.195833 15.5208 0 15.05 0 14.5V2.5C0 1.95 0.195833 1.47917 0.5875 1.0875C0.979167 0.695833 1.45 0.5 2 0.5H18C18.55 0.5 19.0208 0.695833 19.4125 1.0875C19.8042 1.47917 20 1.95 20 2.5V14.5C20 15.05 19.8042 15.5208 19.4125 15.9125C19.0208 16.3042 18.55 16.5 18 16.5H2ZM10 9.5L18 4.5V2.5L10 7.5L2 2.5V4.5L10 9.5Z"
                      fill="#6B7280"
                    />
                  </svg>
                  <input
                    className="contact-form-input dark:bg-jacarta-700 placeholder:text-cement border-jacarta-100 hover:ring-accent/10 focus:ring-accent focus:outline-none focus:outline-0 focus:border-accent dark:border-jacarta-600 dark:placeholder:text-white w-full rounded-xl py-2 sm:py-3 pl-10 hover:ring-2 text-grayish dark:text-white text-sm sm:text-base"
                    required
                    type="email"
                    value={email}
                    placeholder="Email"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailUnverified(false);
                    }}
                  />
                </div>
                <div className="relative mb-3 sm:mb-4">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grayish dark:text-white"
                    width="16"
                    height="21"
                    viewBox="0 0 16 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 21C1.45 21 0.979167 20.8042 0.5875 20.4125C0.195833 20.0208 0 19.55 0 19V9C0 8.45 0.195833 7.97917 0.5875 7.5875C0.979167 7.19583 1.45 7 2 7H3V5C3 3.61667 3.4875 2.4375 4.4625 1.4625C5.4375 0.4875 6.61667 0 8 0C9.38333 0 10.5625 0.4875 11.5375 1.4625C12.5125 2.4375 13 3.61667 13 5V7H14C14.55 7 15.0208 7.19583 15.4125 7.5875C15.8042 7.97917 16 8.45 16 9V19C16 19.55 15.8042 20.0208 15.4125 20.4125C15.0208 20.8042 14.55 21 14 21H2ZM8 16C8.55 16 9.02083 15.8042 9.4125 15.4125C9.80417 15.0208 10 14.55 10 14C10 13.45 9.80417 12.9792 9.4125 12.5875C9.02083 12.1958 8.55 12 8 12C7.45 12 6.97917 12.1958 6.5875 12.5875C6.19583 12.9792 6 13.45 6 14C6 14.55 6.19583 15.0208 6.5875 15.4125C6.97917 15.8042 7.45 16 8 16ZM5 7H11V5C11 4.16667 10.7083 3.45833 10.125 2.875C9.54167 2.29167 8.83333 2 8 2C7.16667 2 6.45833 2.29167 5.875 2.875C5.29167 3.45833 5 4.16667 5 5V7Z"
                      fill="#6B7280"
                    />
                  </svg>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="contact-form-input dark:bg-jacarta-700 border-jacarta-100 hover:ring-accent/10 focus:ring-accent focus:outline-none focus:outline-0 focus:border-accent dark:border-jacarta-600 dark:placeholder:text-white w-full rounded-xl py-2 sm:py-3 pl-10 pr-10 hover:ring-2 text-grayish dark:text-white text-sm sm:text-base"
                    id="password"
                    required
                    value={password}
                    placeholder="Password"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-jacarta-400 hover:text-jacarta-600 dark:text-white dark:hover:text-jacarta-200"
                  >
                    <svg
                      className="fill-cement hover:fill-jacarta-500 transition duration-150"
                      width="22"
                      height="15"
                      viewBox="0 0 22 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <path d="M11 12C12.25 12 13.3125 11.5625 14.1875 10.6875C15.0625 9.8125 15.5 8.75 15.5 7.5C15.5 6.25 15.0625 5.1875 14.1875 4.3125C13.3125 3.4375 12.25 3 11 3C9.75 3 8.6875 3.4375 7.8125 4.3125C6.9375 5.1875 6.5 6.25 6.5 7.5C6.5 8.75 6.9375 9.8125 7.8125 10.6875C8.6875 11.5625 9.75 12 11 12ZM11 10.2C10.25 10.2 9.6125 9.9375 9.0875 9.4125C8.5625 8.8875 8.3 8.25 8.3 7.5C8.3 6.75 8.5625 6.1125 9.0875 5.5875C9.6125 5.0625 10.25 4.8 11 4.8C11.75 4.8 12.3875 5.0625 12.9125 5.5875C13.4375 6.1125 13.7 6.75 13.7 7.5C13.7 8.25 13.4375 8.8875 12.9125 9.4125C12.3875 9.9375 11.75 10.2 11 10.2ZM11 15C8.56667 15 6.35 14.3208 4.35 12.9625C2.35 11.6042 0.9 9.78333 0 7.5C0.9 5.21667 2.35 3.39583 4.35 2.0375C6.35 0.679167 8.56667 0 11 0C13.4333 0 15.65 0.679167 17.65 2.0375C19.65 3.39583 21.1 5.21667 22 7.5C21.1 9.78333 19.65 11.6042 17.65 12.9625C15.65 14.3208 13.4333 15 11 15Z" />
                      ) : (
                        <path d="M18.8 20.6L14.6 16.4501C14.0167 16.6334 13.4292 16.7709 12.8375 16.8626C12.2458 16.9542 11.6333 17 11 17C8.48333 17 6.24167 16.3042 4.275 14.9125C2.30833 13.5209 0.883333 11.7167 0 9.50005C0.35 8.61672 0.791667 7.79588 1.325 7.03755C1.85833 6.27922 2.46667 5.60005 3.15 5.00005L0.4 2.20005L1.8 0.800049L20.2 19.2001L18.8 20.6ZM11 14C11.1833 14 11.3542 13.9917 11.5125 13.975C11.6708 13.9584 11.8417 13.925 12.025 13.875L6.625 8.47505C6.575 8.65838 6.54167 8.82922 6.525 8.98755C6.50833 9.14588 6.5 9.31672 6.5 9.50005C6.5 10.75 6.9375 11.8125 7.8125 12.6875C8.6875 13.5625 9.75 14 11 14ZM18.3 14.4501L15.125 11.3C15.2417 11.0167 15.3333 10.7292 15.4 10.4375C15.4667 10.1459 15.5 9.83338 15.5 9.50005C15.5 8.25005 15.0625 7.18755 14.1875 6.31255C13.3125 5.43755 12.25 5.00005 11 5.00005C10.6667 5.00005 10.3542 5.03338 10.0625 5.10005C9.77083 5.16672 9.48333 5.26672 9.2 5.40005L6.65 2.85005C7.33333 2.56672 8.03333 2.35422 8.75 2.21255C9.46667 2.07088 10.2167 2.00005 11 2.00005C13.5167 2.00005 15.7583 2.69588 17.725 4.08755C19.6917 5.47922 21.1167 7.28338 22 9.50005C21.6167 10.4834 21.1125 11.3959 20.4875 12.2375C19.8625 13.0792 19.1333 13.8167 18.3 14.4501ZM13.675 9.85005L10.675 6.85005C11.1417 6.76672 11.5708 6.80422 11.9625 6.96255C12.3542 7.12088 12.6917 7.35005 12.975 7.65005C13.2583 7.95005 13.4625 8.29588 13.5875 8.68755C13.7125 9.07922 13.7417 9.46672 13.675 9.85005Z" />
                      )}
                    </svg>
                  </button>
                </div>
                {emailUnverified && (
                  <ResendVerificationNotice
                    email={email.trim()}
                    description="Your email address hasn't been verified yet, so you can't sign in. Open the verification link we emailed you — or send yourself a new one."
                  />
                )}

                <div className="flex justify-end mb-4 sm:mb-6 lg:mb-8">
                  <Link
                    href="/forget-password"
                    className="text-sm text-accent hover:text-accent-dark underline"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="dark:bg-accent bg-accent text-white dark:hover:bg-accent-dark hover:bg-accent-dark mb-3 sm:mb-4 flex w-full items-center justify-center rounded-xl py-2 sm:py-3 px-8 text-center transition-all hover:border-transparent hover:text-white dark:text-white dark:hover:border-transparent text-sm sm:text-base"
                >
                  {isLoading ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : null}
                  {isLoading ? "Signing in..." : "Sign in"}
                </button>
              </form>

              {/* <div className="flex items-center my-3 sm:my-4 lg:my-5 justify-center">
                <span className="mx-4 text-grayish dark:text-white">or</span>
              </div> */}

              {/* <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className=" dark:border-accent border-2 mb-8 sm:mb-12 lg:mb-16 border-jacarta-100 hover:border-accent flex w-full items-center justify-center rounded-xl py-2 sm:py-3 px-8 text-center transition-all bg-white hover:bg-gray-50 dark:bg-jacarta-700 dark:hover:bg-jacarta-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="currentColor"
                >
                  <path
                    d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96l-3.98 3.09C3.515 21.3 7.565 24 12.255 24z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.66h-3.98a11.86 11.86 0 000 10.68l3.98-3.05z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C18.205 1.19 15.495 0 12.255 0c-4.69 0-8.74 2.7-10.71 6.66l3.98 3.09c.95-2.85 3.6-5 6.73-5z"
                    fill="#EA4335"
                  />
                </svg>
                <span className="ml-2 text-cement dark:text-white">
                  Sign in with Google
                </span>
              </button> */}

              <div className="text-center border-jacarta-100 mt-6">
                <span className="text-sm text-grayish dark:text-white">
                  Don't have an account?{" "}
                </span>
                <Link
                  href="/sign-up"
                  className="text-sm text-accent hover:text-accent-dark underline font-bold"
                >
                  Create one
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignIn;
